import type { CaseSummary, Payment } from '@/types/domain'

/* A payment's party — whom we paid, or whom we received from — as the two
 * payment forms hold it and as the wire takes it (S-22d2, migration 0080).
 *
 * A module rather than part of `PaymentPartyPicker.tsx` because a component
 * file may export only components, and both sheets need these rules.
 *
 * The rule is the server's (`finance/payment_party.go`); it is mirrored here
 * only so a form can refuse before it sends:
 *
 *   · OUTBOUND names a record — a directory counterparty by id, or the case's
 *     own client by id (a refund). A typed name is not a party: the server
 *     answers `counterpartyId: required`. The one exception is an amend of a
 *     row typed before the link existed, which may be echoed back EXACTLY as
 *     stored (rows already stored are never rewritten).
 *   · INBOUND names the case's client by id, or a payer typed as the bank
 *     printed it. Never a directory counterparty (`counterpartyId: direction`).
 *
 * Whatever the form names, the stored `counterpartyName` of a record is the
 * RECORD's name, written by the server: the name sent beside an id is ignored
 * there, and is sent only because the field is still on the wire. */

export type PaymentPartyKind = 'client' | 'counterparty' | 'typed'

/** What the party picker emits. `id` is the record's id for `client` and
 *  `counterparty`, and '' for `typed`; a `counterparty` with id '' is "nobody
 *  picked yet". */
export interface PaymentParty {
  kind: PaymentPartyKind
  id: string
  name: string
}

/** The case's client as the payment forms offer it — read off the case the
 *  payment moves against, never typed. */
export interface CaseClient {
  id: string
  name: string
}

/** Nobody picked: what an outbound payment starts with. */
export const NO_PARTY: PaymentParty = { kind: 'counterparty', id: '', name: '' }

/** The case's client, when the case says which. `CaseSummary.clientId` is
 *  optional on the wire, and a case that does not carry it offers no client
 *  row and preselects nobody — a client matched by NAME would be the guess
 *  this whole change exists to stop. */
export function caseClientOf(
  record: Pick<CaseSummary, 'clientId' | 'clientName'> | undefined,
): CaseClient | undefined {
  if (!record?.clientId) return undefined
  return { id: record.clientId, name: record.clientName }
}

/** The party a NEW payment starts with in a direction: inbound money is the
 *  case's client's until the reader says otherwise; outbound money names
 *  nobody until the reader picks — the common outbound party is a haulier,
 *  and a refund preselected would be the rare case guessed. */
export function seedParty(
  direction: Payment['direction'],
  client: CaseClient | undefined,
): PaymentParty {
  if (direction === 'out') return NO_PARTY
  return client ? { kind: 'client', id: client.id, name: client.name } : typed('')
}

/** The party a stored payment names, as served. A payment whose party is
 *  withheld (AD-6) arrives with neither id and a '' name: that reads as a blank
 *  typed party, and the forms lock the field before it matters. */
export function storedParty(
  payment: Pick<Payment, 'counterpartyName' | 'counterpartyId' | 'clientId'>,
): PaymentParty {
  if (payment.counterpartyId) {
    return { kind: 'counterparty', id: payment.counterpartyId, name: payment.counterpartyName }
  }
  if (payment.clientId) {
    return { kind: 'client', id: payment.clientId, name: payment.counterpartyName }
  }
  return typed(payment.counterpartyName)
}

/** Whether `party` may be sent for a payment in `direction`.
 *
 *  `stored` is an amend's stored party: an outbound row typed before the link
 *  existed saves unchanged when its name is echoed exactly, and only then. */
export function partyComplete(
  direction: Payment['direction'],
  party: PaymentParty,
  stored?: PaymentParty,
): boolean {
  if (direction === 'out') {
    if (party.kind !== 'typed') return party.id !== ''
    return stored?.kind === 'typed' && stored.name.trim() !== '' && party.name === stored.name
  }
  if (party.kind === 'client') return party.id !== ''
  if (party.kind === 'typed') return party.name.trim() !== ''
  return false
}

/** The party's wire fields: exactly one shape — a counterparty id, a client
 *  id, or a typed name with no id at all. */
export function partyPayload(party: PaymentParty): {
  counterpartyName: string
  counterpartyId?: string
  clientId?: string
} {
  const counterpartyName = party.name.trim()
  if (party.kind === 'counterparty' && party.id !== '') {
    return { counterpartyName, counterpartyId: party.id }
  }
  if (party.kind === 'client' && party.id !== '') {
    return { counterpartyName, clientId: party.id }
  }
  return { counterpartyName }
}

function typed(name: string): PaymentParty {
  return { kind: 'typed', id: '', name }
}
